import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useApi } from '../ApiContext';
import Gallery from './Gallery'; 
import './ManageProduct.css';

// =========================================================================
// 1. ProductActions Component (Buttons)
// =========================================================================
const ProductActions = React.memo(({ openModal }) => (
    <div className="button-group">
        <button type="button" className="add-button" onClick={() => openModal('addProduct')}>
            Add Product
        </button>
        <button type="button" className="add-button" onClick={() => openModal('addItem')}>
            Add Item/Variant
        </button>
    </div>
));

// =========================================================================
// 2. ProductDisplay Component (Main Table)
// =========================================================================
const ProductDisplay = React.memo(({ products, openModal, handleDeleteProduct }) => (
    <table className="product-table">
        <thead>
            <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
            </tr>
        </thead>
        <tbody>
            {products.length === 0 ? (
                <tr>
                    <td colSpan="3" style={{ textAlign: 'center' }}>
                        No products found. Add a new product to begin.
                    </td>
                </tr>
            ) : (
                products.map(product => (
                    <tr key={product.id}>
                        <td>{product.name}</td>
                        <td>{product.description || 'N/A'}</td>
                        <td className="product-actions-cell">
                            <button type="button" onClick={() => openModal('editProduct', product)}>Edit</button>
                            <button type="button" onClick={() => handleDeleteProduct(product.id)}>Delete</button>
                            <button type="button" onClick={() => openModal('manageItems', product)}>Manage Items</button>
                        </td>
                    </tr>
                ))
            )}
        </tbody>
    </table>
));

// =========================================================================
// 3. ManageProduct Component (Parent Logic)
// =========================================================================
const ManageProduct = () => {
    const { api } = useApi();
    
    // Core States
    const [products, setProducts] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [modalType, setModalType] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [formToReturnTo, setFormToReturnTo] = useState(null);

    const [selectedItemForEdit, setSelectedItemForEdit] = useState({
        itemId: null, duration: '', price: '', reseller_price: ''
    });

    const [formProductData, setFormProductData] = useState({
        name: '', description: '', image_link: ''
    });
    const [formItemData, setFormItemData] = useState({
        selectedProduct: '', duration: '', price: '', reseller_price: '', codes: ''
    });
    const [customDuration, setCustomDuration] = useState('');

    const durationOptions = ["1 months", "3 months", "6 months", "1 years", "30 days"];

    // Fetch Products
    const fetchProducts = useCallback(async () => {
        if (!api) return;
        try {
            const res = await api.get('/products/admin');
            setAllProducts(res.data);
            const uniqueProducts = res.data.map(p => ({
                id: p.id, name: p.name, description: p.description, 
                image_link: p.image_link, product_data: p.product_data
            }));
            setProducts(uniqueProducts);
        } catch (err) {
            toast.error("Failed to load products.");
        }
    }, [api]);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    // ✨ AUTO-FILL LOGIC
    useEffect(() => {
        if (modalType === 'addItem' && formItemData.selectedProduct && formItemData.duration && formItemData.duration !== 'custom') {
            const product = allProducts.find(p => p.id === parseInt(formItemData.selectedProduct));
            const selectedDur = formItemData.duration;
            
            if (product?.product_data) {
                let existingVariant = product.product_data[selectedDur] || product.product_data[`${selectedDur}s`] || product.product_data[selectedDur.slice(0, -1)];
                if (existingVariant) {
                    setFormItemData(prev => ({
                        ...prev,
                        price: existingVariant.price ?? 0,
                        reseller_price: existingVariant.reseller_price ?? 0
                    }));
                }
            }
        }
    }, [formItemData.selectedProduct, formItemData.duration, allProducts, modalType]);

    // Modal Control
    const openModal = (type, productToEdit = null) => {
        setEditingProduct(null);
        setFormProductData({ name: '', description: '', image_link: '' });
        setFormItemData({ selectedProduct: '', duration: '', price: '', reseller_price: '', codes: '' });
        setSelectedItemForEdit({ itemId: null, duration: '', price: '', reseller_price: '' });
        setCustomDuration('');
        setModalType(type);
        
        if (productToEdit) {
            const fullProduct = allProducts.find(p => p.id === productToEdit.id) || productToEdit;
            setEditingProduct(fullProduct);
            if (type === 'editProduct') {
                setFormProductData({
                    name: fullProduct.name,
                    description: fullProduct.description,
                    image_link: fullProduct.image_link || ''
                });
            }
        }
    };

    const closeModal = () => { setModalType(null); setEditingProduct(null); setIsGalleryOpen(false); };

    // Gallery Handlers
    const openGallery = (formType) => { setFormToReturnTo(formType); setModalType(null); setIsGalleryOpen(true); };
    const handleImageSelect = (imageUrl) => {
        setFormProductData(prev => ({ ...prev, image_link: imageUrl }));
        setModalType(formToReturnTo);
        setIsGalleryOpen(false);
    };

    // Change Handlers
    const handleProductFormChange = (e) => setFormProductData({...formProductData, [e.target.name]: e.target.value});
    const handleItemFormChange = (e) => setFormItemData({...formItemData, [e.target.name]: e.target.value});
    
    const handleDurationSelect = (e) => {
        const dur = e.target.value;
        const item = editingProduct?.product_data?.[dur];
        setSelectedItemForEdit({
            itemId: item?.item_id || null,
            duration: dur,
            price: item?.price ?? '',
            reseller_price: item?.reseller_price ?? ''
        });
    };

    const handlePriceChange = (e) => setSelectedItemForEdit({...selectedItemForEdit, [e.target.name]: e.target.value});

    // Submission Handlers
    const handleProductSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.post('/products', formProductData);
            toast.success("Product added!");
            fetchProducts(); closeModal();
        } catch { toast.error("Error creating product."); }
    };

    const handleAddItemSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            productId: formItemData.selectedProduct,
            duration: formItemData.duration === 'custom' ? customDuration : formItemData.duration,
            price: parseFloat(formItemData.price) || 0,
            resellerPrice: parseFloat(formItemData.reseller_price) || 0,
            codes: formItemData.codes 
        };
        try {
            await api.post('/products/item', payload);
            toast.success("Codes added successfully!");
            fetchProducts(); closeModal();
        } catch (err) { toast.error(err.response?.data?.message || "Failed to add item"); }
    };

    const handleItemEditSubmit = async (e) => {
        e.preventDefault();
        const payload = { price: parseFloat(selectedItemForEdit.price), resellerPrice: parseFloat(selectedItemForEdit.reseller_price) };
        try {
            await api.put(`/products/item/${selectedItemForEdit.itemId}`, payload);
            toast.success("Prices updated!");
            fetchProducts(); closeModal();
        } catch { toast.error("Error updating prices."); }
    };

    const handleDeleteItem = async (id) => {
        if (!window.confirm('Delete this variant?')) return;
        try {
            await api.delete(`/products/item/${id}`);
            toast.success("Variant deleted!");
            fetchProducts(); closeModal();
        } catch { toast.error("Delete failed."); }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm('Delete entire product?')) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success("Product deleted!");
            fetchProducts();
        } catch { toast.error("Delete failed."); }
    };

    const renderModal = () => {
        if (modalType === 'addProduct' || modalType === 'editProduct') {
            const isEdit = modalType === 'editProduct';
            return (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button type="button" className="close-button" onClick={closeModal}>&times;</button>
                        <form onSubmit={isEdit ? async (e) => {
                                e.preventDefault();
                                try { await api.put(`/products/${editingProduct.id}`, formProductData); toast.success("Updated!"); fetchProducts(); closeModal(); } 
                                catch { toast.error("Update failed."); }
                            } : handleProductSubmit}>
                            <h2>{isEdit ? 'Edit Product Metadata' : 'Add New Product'}</h2>
                            <div><label>Name:</label><input type="text" name="name" value={formProductData.name} onChange={handleProductFormChange} required /></div>
                            <div><label>Description:</label><textarea name="description" value={formProductData.description} onChange={handleProductFormChange}></textarea></div>
                            <div>
                                <label>Image Link:</label>
                                <div className="image-input-group">
                                    <input type="text" name="image_link" value={formProductData.image_link} onChange={handleProductFormChange} />
                                    <button type="button" className="gallery-button" onClick={() => openGallery(modalType)}>Gallery</button>
                                </div>
                                {formProductData.image_link && <img src={formProductData.image_link} alt="Preview" className="image-preview" />}
                            </div>
                            <button type="submit">{isEdit ? 'Update Product' : 'Add Product'}</button>
                        </form>
                    </div>
                </div>
            );
        }

        if (modalType === 'addItem') {
            return (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button type="button" className="close-button" onClick={closeModal}>&times;</button>
                        <form onSubmit={handleAddItemSubmit}>
                            <h2>Add / Refill Codes</h2>
                            <div><label>Product:</label>
                                <select className="selectedProduct" name="selectedProduct" value={formItemData.selectedProduct} onChange={handleItemFormChange} required>
                                    <option value="">-- Select Product --</option>
                                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div><label>Duration:</label>
                                <select className="selectedProduct" name="duration" value={formItemData.duration} onChange={handleItemFormChange} required>
                                    <option value="">-- Select Duration --</option>
                                    {durationOptions.map(o => <option key={o} value={o}>{o}</option>)}
                                    <option value="custom">Custom Duration</option>
                                </select>
                            </div>
                            {formItemData.duration === 'custom' && (<div><label>Custom Duration:</label><input type="text" value={customDuration} onChange={(e) => setCustomDuration(e.target.value)} required /></div>)}
                            <div><label>Customer Price:</label><input type="number" name="price" value={formItemData.price} onChange={handleItemFormChange} step="0.01" required /></div>
                            <div><label>Reseller Price:</label><input type="number" name="reseller_price" value={formItemData.reseller_price} onChange={handleItemFormChange} step="0.01" required /></div>
                            <div><label>Codes:</label><textarea name="codes" value={formItemData.codes} onChange={handleItemFormChange} required></textarea></div>
                            <button type="submit">Submit Item</button>
                        </form>
                    </div>
                </div>
            );
        }

        if (modalType === 'manageItems') {
            const items = editingProduct?.product_data || {};
            const itemKeys = Object.keys(items);
            return (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button type="button" className="close-button" onClick={closeModal}>&times;</button>
                        <h2>Manage: {editingProduct?.name}</h2>
                        <form onSubmit={handleItemEditSubmit}>
                            <div className="form-group">
                                <label>Select Period:</label>
                                <select className="selectedProduct" value={selectedItemForEdit.duration} onChange={handleDurationSelect} required>
                                    <option value="">-- Select Period --</option>
                                    {itemKeys.map(k => <option key={k} value={k}>{k} (Stock: {items[k].count})</option>)}
                                </select>
                            </div>
                            {selectedItemForEdit.itemId && (
                                <>
                                    <div className="form-group"><label>Customer Price:</label><input type="number" name="price" value={selectedItemForEdit.price} onChange={handlePriceChange} step="0.01" required /></div>
                                    <div className="form-group"><label>Reseller Price:</label><input type="number" name="reseller_price" value={selectedItemForEdit.reseller_price} onChange={handlePriceChange} step="0.01" required /></div>
                                    <button type="button" className="modal-delete-item" onClick={() => handleDeleteItem(selectedItemForEdit.itemId)}>Delete Variant</button>
                                    <button type="submit">Update Prices</button>
                                </>
                            )}
                        </form>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="AddProduct">
            <h1>Product Management</h1>
            <ProductActions openModal={openModal} />
            <div className="product-page-container">
                <ProductDisplay products={products} openModal={openModal} handleDeleteProduct={handleDeleteProduct} />
                {modalType && renderModal()}
                {isGalleryOpen && <Gallery onSelectImage={handleImageSelect} onClose={() => { setIsGalleryOpen(false); setModalType(formToReturnTo); }} />}
            </div>
        </div>
    );
};

export default ManageProduct;